import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { sendAppointment, checkAvailability } from "@/lib/api";
import { SERVICES, TIME_SLOTS } from "@/data/content";

export const BookingForm = ({ compact = false }) => {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: SERVICES[0],
    date: "",
    time: "10:00",
    message: "",
    consent: false,
  });
  const [sending, setSending] = useState(false);
  const [slotTaken, setSlotTaken] = useState(false);

  useEffect(() => {
    const pre = searchParams.get("service");
    if (pre && SERVICES.includes(pre)) setForm((f) => ({ ...f, service: pre }));
  }, [searchParams]);

  useEffect(() => {
    if (!form.date || !form.time) {
      setSlotTaken(false);
      return;
    }
    let active = true;
    checkAvailability(form.date, form.time)
      .then(({ data }) => { if (active) setSlotTaken(!data.available); })
      .catch(() => { if (active) setSlotTaken(false); });
    return () => { active = false; };
  }, [form.date, form.time]);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Bitte stimmen Sie der Datenverarbeitung zu.");
      return;
    }
    setSending(true);
    try {
      await sendAppointment({
        name: form.name,
        phone: form.phone,
        email: form.email,
        service: form.service,
        date: form.date,
        time: form.time,
        message: form.message,
      });
      toast.success("Vielen Dank! Ihre Anfrage ist eingegangen – wir rufen Sie zur Bestätigung kurz an.");
      setForm({ name: "", phone: "", email: "", service: SERVICES[0], date: "", time: "10:00", message: "", consent: false });
    } catch (err) {
      toast.error("Das hat leider nicht geklappt. Rufen Sie uns gerne direkt an: 02238 43 770.");
    } finally {
      setSending(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <form onSubmit={submit} className={`bg-[#FFFFFF] border border-[#24211D]/15 ${compact ? "p-6 sm:p-8" : "p-8 sm:p-10"} space-y-6`} data-testid="booking-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label htmlFor="booking-name" className="nes-label">Name *</label>
          <input id="booking-name" data-testid="booking-name-input" type="text" required className="nes-input" placeholder="Ihr Name" value={form.name} onChange={set("name")} />
        </div>
        <div>
          <label htmlFor="booking-phone" className="nes-label">Telefonnummer *</label>
          <input id="booking-phone" data-testid="booking-phone-input" type="tel" required className="nes-input" placeholder="02238 ..." value={form.phone} onChange={set("phone")} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label htmlFor="booking-service" className="nes-label">Leistung *</label>
          <select id="booking-service" data-testid="booking-service-select" required className="nes-input" value={form.service} onChange={set("service")}>
            {SERVICES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="booking-date" className="nes-label">Wunschtag *</label>
            <input id="booking-date" data-testid="booking-date-input" type="date" required min={today} className="nes-input" value={form.date} onChange={set("date")} />
          </div>
          <div>
            <label htmlFor="booking-time" className="nes-label">Uhrzeit *</label>
            <select id="booking-time" data-testid="booking-time-select" required className="nes-input" value={form.time} onChange={set("time")}>
              {TIME_SLOTS.map((t) => (
                <option key={t} value={t}>{t} Uhr</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div>
        <label htmlFor="booking-email" className="nes-label">E-Mail (optional – für die Bestätigung)</label>
        <input id="booking-email" data-testid="booking-email-input" type="email" className="nes-input" placeholder="ihre@email.de" value={form.email} onChange={set("email")} />
        <p className="text-[10px] text-[#24211D]/50 font-sans mt-1.5">Wenn Sie Ihre E-Mail angeben, schreiben wir Ihnen automatisch, sobald Ihr Termin angenommen wurde.</p>
      </div>

      {slotTaken && (
        <p className="text-[11px] font-sans text-[#B08D57] border border-[#B08D57]/40 bg-[#B08D57]/5 px-3 py-2" data-testid="slot-taken-hint">
          Dieser Zeitraum ist bereits vergeben. Wählen Sie gerne eine andere Zeit – oder senden Sie die Anfrage trotzdem ab, wir rufen Sie mit einer Alternative zurück.
        </p>
      )}

      <div>
        <label htmlFor="booking-message" className="nes-label">Nachricht (optional)</label>
        <textarea id="booking-message" data-testid="booking-message-input" rows={3} className="nes-input resize-none" placeholder="z. B. kurzfristiger Termin gewünscht, aktuelle Haarfarbe ..." value={form.message} onChange={set("message")} />
      </div>

      <label className="flex items-start gap-3 cursor-pointer" data-testid="booking-consent-label">
        <input type="checkbox" data-testid="booking-consent-checkbox" checked={form.consent} onChange={set("consent")} className="mt-1 accent-[#B08D57]" />
        <span className="text-[11px] text-[#24211D]/70 leading-relaxed font-sans">
          Ich stimme zu, dass meine Angaben zur Terminvereinbarung verarbeitet werden. Keine Weitergabe an Dritte. Details in der Datenschutzerklärung. *
        </span>
      </label>

      <div className="pt-2 text-center">
        <button
          type="submit"
          disabled={sending}
          data-testid="booking-form-submit-button"
          className="w-full sm:w-auto bg-[#B08D57] hover:bg-[#967442] disabled:opacity-60 text-[#FBF8F3] px-10 py-4 text-xs uppercase tracking-[0.2em] font-sans transition-colors duration-300"
        >
          {sending ? "Wird gesendet ..." : "Terminanfrage absenden"}
        </button>
      </div>
    </form>
  );
};
